import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getTask } from "../api/admin";
import { evaluateTask, finishJobs, setTaskState, TASK_POLL_MS, useJobs, type Job, type JobKind } from "../api/jobs";
import { invalidatePipeline, keys } from "../api/queries";

// Mounted once in the app shell: polls GET /tasks/{id} for every queued job
// this browser started, whatever page is open. One task can cover several
// kinds (the per-course pipeline runs sync + extract + index as one task).
export function JobWatcher() {
  const jobs = useJobs();
  const byTask = new Map<string, Job[]>();
  for (const job of jobs) {
    if (!job.taskId) continue;
    byTask.set(job.taskId, [...(byTask.get(job.taskId) ?? []), job]);
  }
  return (
    <>
      {[...byTask.entries()].map(([taskId, taskJobs]) => (
        <TaskPoller
          key={taskId}
          taskId={taskId}
          kinds={taskJobs.map((j) => j.kind)}
          startedAt={Math.min(...taskJobs.map((j) => j.startedAt))}
        />
      ))}
    </>
  );
}

function TaskPoller({ taskId, kinds, startedAt }: { taskId: string; kinds: JobKind[]; startedAt: number }) {
  const qc = useQueryClient();
  const task = useQuery({
    queryKey: keys.task(taskId),
    queryFn: () => getTask(taskId),
    refetchInterval: (q) => (q.state.data?.ready ? false : TASK_POLL_MS),
    refetchIntervalInBackground: true,
    retry: 2,
  });
  const kindsKey = kinds.join(",");

  useEffect(() => {
    const kindList = kindsKey.split(",") as JobKind[];
    if (task.error) {
      // 503 = result backend down; anything else is just as unanswerable.
      finishJobs(kindList, {
        taskId,
        status: "unknown",
        runId: null,
        result: null,
        error: `Couldn't read the job's status (${task.error.message}). Check the run list for its outcome.`,
      });
      invalidatePipeline(qc);
      return;
    }
    if (task.data) setTaskState(taskId, task.data.state);
    const verdict = evaluateTask({ startedAt }, task.data);
    if (!verdict) return;
    for (const kind of kindList) {
      // The course pipeline's result nests one { run_id, status } per step.
      const step = verdict.result?.[kind] as { run_id?: number; status?: string } | undefined;
      finishJobs([kind], {
        ...verdict,
        taskId,
        runId: step?.run_id ?? verdict.runId,
        status: step?.status ?? verdict.status,
      });
    }
    invalidatePipeline(qc);
  }, [task.data, task.dataUpdatedAt, task.error, taskId, kindsKey, startedAt, qc]);

  return null;
}
